import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { CalendarEventDialog } from '../components/CalendarEventDialog';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { useAuth } from '../contexts/AuthContext';
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Plus,
  Phone,
  Video,
  Calendar as CalendarIcon,
  FileSpreadsheet,
  List,
  Grid3x3,
  Ticket as TicketIcon,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_META = {
  evento: { icon: CalendarIcon, color: 'bg-sky-500', text: 'text-sky-600', light: 'bg-sky-50' },
  reuniao: { icon: Video, color: 'bg-violet-500', text: 'text-violet-600', light: 'bg-violet-50' },
  chamada: { icon: Phone, color: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50' },
  ticket: { icon: TicketIcon, color: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50' },
};

const CAN_CREATE_ROLES = ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice', 'comercial', 'chefe_equipa'];

export const CalendarPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('month'); // 'month' | 'list'
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);

  const canCreate = CAN_CREATE_ROLES.includes(user?.role);

  const fetchEvents = useCallback(async (month) => {
    setLoading(true);
    try {
      const from = format(startOfMonth(month), 'yyyy-MM-dd');
      const to = format(endOfMonth(month), 'yyyy-MM-dd') + 'T23:59';
      const res = await axios.get(`${API_URL}/calendar/events`, { params: { from, to } });
      setEvents(res.data || []);
    } catch (err) {
      console.error(err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(currentMonth); }, [currentMonth, fetchEvents]);
  useRefreshListener(() => fetchEvents(currentMonth));

  // Open event dialog from ?event=<id> query param (deep link from notifications)
  useEffect(() => {
    const evId = searchParams.get('event');
    if (evId) {
      (async () => {
        try {
          const r = await axios.get(`${API_URL}/calendar/event/${evId}`);
          setActiveEvent(r.data);
          setDialogOpen(true);
          // Clear query param after opening
          searchParams.delete('event');
          setSearchParams(searchParams, { replace: true });
        } catch { /* ignore — may be a ticket */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return events;
    if (filterType === 'ticket') return events.filter(e => e.kind === 'ticket');
    return events.filter(e => e.tipo === filterType);
  }, [events, filterType]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const e of filteredEvents) {
      if (!e.datetime) continue;
      try {
        const d = parseISO(e.datetime);
        const key = format(d, 'yyyy-MM-dd');
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({ ...e, date: d });
      } catch { /* ignore */ }
    }
    return map;
  }, [filteredEvents]);

  const selectedKey = format(selectedDate, 'yyyy-MM-dd');
  const dayEvents = (eventsByDay.get(selectedKey) || []).sort((a, b) => a.date - b.date);

  const openEventDialog = async (ev) => {
    if (ev.kind === 'ticket') {
      navigate(ev.link);
      return;
    }
    try {
      const r = await axios.get(`${API_URL}/calendar/event/${ev.id}`);
      setActiveEvent(r.data);
      setDialogOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const openCreateDialog = (date = selectedDate) => {
    const baseHour = new Date();
    baseHour.setMinutes(0, 0, 0);
    baseHour.setHours(baseHour.getHours() + 1);
    const dt = new Date(date);
    dt.setHours(baseHour.getHours(), 0, 0, 0);
    setDefaultDate(format(dt, "yyyy-MM-dd'T'HH:mm"));
    setActiveEvent(null);
    setDialogOpen(true);
  };

  const handleExport = async () => {
    try {
      const from = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const to = format(endOfMonth(currentMonth), 'yyyy-MM-dd') + 'T23:59';
      const r = await axios.get(`${API_URL}/calendar/events/export`, {
        params: { from, to },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `eventos_${format(currentMonth, 'yyyy-MM')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <CalendarDays className="w-6 h-6 text-[#2D3E6F]" />
              Calendário
            </h1>
            <p className="text-slate-500 mt-1">
              {format(currentMonth, "MMMM 'de' yyyy", { locale: pt })} · {filteredEvents.length} agendamento{filteredEvents.length === 1 ? '' : 's'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="evento">Eventos</SelectItem>
                <SelectItem value="reuniao">Reuniões</SelectItem>
                <SelectItem value="chamada">Chamadas</SelectItem>
                <SelectItem value="ticket">Tickets</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex bg-slate-100 rounded-full p-0.5">
              <button
                type="button"
                onClick={() => setView('month')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${view === 'month' ? 'bg-white text-[#2D3E6F] shadow-sm' : 'text-slate-500'}`}
                data-testid="view-month"
              >
                <Grid3x3 className="w-3.5 h-3.5" /> Mês
              </button>
              <button
                type="button"
                onClick={() => setView('list')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${view === 'list' ? 'bg-white text-[#2D3E6F] shadow-sm' : 'text-slate-500'}`}
                data-testid="view-list"
              >
                <List className="w-3.5 h-3.5" /> Lista
              </button>
            </div>

            <Button type="button" variant="outline" size="sm" onClick={handleExport} className="gap-1" data-testid="export-excel-button">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </Button>

            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="rounded-full h-9 w-9">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => { const t = new Date(); setCurrentMonth(t); setSelectedDate(t); }} className="rounded-full h-9">Hoje</Button>
              <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="rounded-full h-9 w-9">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {canCreate && (
              <Button
                onClick={() => openCreateDialog(selectedDate)}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-1"
                data-testid="create-event-button"
              >
                <Plus className="w-4 h-4" /> Novo Evento
              </Button>
            )}
          </div>
        </div>

        {view === 'month' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Calendar */}
            <Card className="lg:col-span-3 rounded-2xl border-slate-100">
              <CardContent className="p-6">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => d && setSelectedDate(d)}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  locale={pt}
                  className="w-full"
                  modifiers={{
                    hasEvents: (d) => eventsByDay.has(format(d, 'yyyy-MM-dd')),
                  }}
                  modifiersClassNames={{
                    hasEvents: 'relative font-semibold after:content-[""] after:absolute after:bottom-1.5 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-[#FF6B35]',
                  }}
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4 w-full",
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_label: "text-lg font-semibold",
                    nav: "space-x-1 flex items-center",
                    table: "w-full border-collapse",
                    head_row: "flex w-full",
                    head_cell: "text-slate-500 rounded-md w-full font-medium text-sm py-2",
                    row: "flex w-full mt-2",
                    cell: "w-full text-center text-sm p-0 relative",
                    day: "h-12 w-full p-0 font-normal hover:bg-slate-100 rounded-lg transition-colors",
                    day_selected: "bg-[#2D3E6F] text-white hover:bg-[#1a2a4a]",
                    day_today: "bg-[#4DD4E8]/20 text-[#0e7490]",
                  }}
                />
              </CardContent>
            </Card>

            {/* Selected Day Events */}
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center justify-between">
                  <span>{format(selectedDate, "dd 'de' MMM", { locale: pt })}</span>
                  {canCreate && (
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openCreateDialog(selectedDate)} title="Criar evento neste dia" data-testid="quick-create-day">
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </CardTitle>
                <p className="text-xs text-slate-400">
                  {dayEvents.length} {dayEvents.length === 1 ? 'agendamento' : 'agendamentos'}
                </p>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="day-events">
                {loading ? (
                  <p className="text-sm text-slate-400 text-center py-4">A carregar...</p>
                ) : dayEvents.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-400 mb-3">Sem eventos neste dia</p>
                    {canCreate && (
                      <Button variant="outline" size="sm" onClick={() => openCreateDialog(selectedDate)}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Agendar evento
                      </Button>
                    )}
                  </div>
                ) : (
                  dayEvents.map(ev => {
                    const meta = TYPE_META[ev.kind === 'ticket' ? 'ticket' : ev.tipo] || TYPE_META.evento;
                    const Icon = meta.icon;
                    return (
                      <button
                        key={`${ev.kind}-${ev.id}`}
                        type="button"
                        onClick={() => openEventDialog(ev)}
                        className={`w-full text-left p-3 rounded-lg border border-slate-100 hover:border-[#2D3E6F]/30 hover:bg-slate-50 transition-all group ${meta.light}`}
                        data-testid={`event-${ev.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <Icon className={`w-4 h-4 ${meta.text} mt-0.5 flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-xs font-semibold text-slate-700">{format(ev.date, 'HH:mm')}</span>
                              <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-[#2D3E6F]" />
                            </div>
                            <p className="text-sm font-medium text-slate-800 truncate">{ev.title || ev.tipo_label}</p>
                            {ev.contract_name && (
                              <p className="text-xs text-slate-500 truncate mt-0.5">{ev.contract_name}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-1 mt-1.5">
                              <StatusBadge status={ev.status} />
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {view === 'list' && (
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-0 divide-y divide-slate-100">
              {loading ? (
                <p className="text-sm text-slate-400 text-center py-8">A carregar...</p>
              ) : filteredEvents.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Sem agendamentos neste mês</p>
              ) : (
                filteredEvents
                  .slice()
                  .sort((a, b) => (a.datetime || '').localeCompare(b.datetime || ''))
                  .map(ev => {
                    const meta = TYPE_META[ev.kind === 'ticket' ? 'ticket' : ev.tipo] || TYPE_META.evento;
                    const Icon = meta.icon;
                    let dateStr = '-';
                    try {
                      dateStr = format(parseISO(ev.datetime), "dd MMM yyyy HH:mm", { locale: pt });
                    } catch { /* ignore */ }
                    return (
                      <button
                        key={`${ev.kind}-${ev.id}`}
                        type="button"
                        onClick={() => openEventDialog(ev)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                        data-testid={`list-event-${ev.id}`}
                      >
                        <div className={`w-9 h-9 rounded-full ${meta.light} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-4 h-4 ${meta.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{ev.title || ev.tipo_label}</p>
                          <p className="text-xs text-slate-500">{dateStr} · {ev.tipo_label}{ev.contract_name ? ` · ${ev.contract_name}` : ''}</p>
                        </div>
                        <StatusBadge status={ev.status} />
                        <ExternalLink className="w-4 h-4 text-slate-300" />
                      </button>
                    );
                  })
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <CalendarEventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        event={activeEvent}
        defaultDate={defaultDate}
        onSaved={() => fetchEvents(currentMonth)}
      />
    </Layout>
  );
};

export default CalendarPage;
